import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROLES, type Client } from "@salesboard/contracts";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table/DataTable";
import { ListToolbar } from "@/components/ui/data-table/ListToolbar";
import { PaginationControl } from "@/components/ui/data-table/PaginationControl";
import { EmptyState, ErrorState, LoadingState, NoResultState } from "@/components/ui/states";
import { StatusChip } from "@/components/ui/StatusChip";
import { useServerTable, type FilterDefs } from "@/hooks/useServerTable";
import { ApiClientError } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useOpenDetail } from "@/app/useOpenDetail";
import { useSession } from "@/features/auth/useSession";
import { ClientFormDialog } from "./ClientFormDialog";
import { useClients } from "./hooks";

export function ClientsPage(): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const openDetail = useOpenDetail();
  const session = useSession();

  useDocumentTitle(t("clients.title"));

  // Inside the component now that its labels come from the catalogue, and memoised because
  // `useServerTable` and `useFilterSet` key their memos on the definitions object.
  const FILTERS: FilterDefs = useMemo(
    () => ({
      q: { type: "search" },
      active: {
        type: "select",
        label: t("filter.state"),
        options: [
          { value: "true", label: t("clients.active") },
          { value: "false", label: t("clients.inactive") },
        ],
      },
    }),
    [t],
  );

  const table = useServerTable("clients", FILTERS, { field: "name", direction: "asc" });
  const query = useClients(table.queryParams);
  const [createOpen, setCreateOpen] = useState(false);

  const columns: Column<Client>[] = [
    {
      key: "name",
      header: t("column.clientName"),
      sortable: true,
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: "active",
      header: t("column.state"),
      sortable: true,
      // The shared chip: never colour alone — the state is a word, and the colour only
      // reinforces it. A chip rather than tinted text because a state is a *label*, which is
      // what tells it apart from the numbers in the columns either side.
      render: (row) => (
        <StatusChip on={row.active} label={row.active ? t("clients.active") : t("clients.inactive")} />
      ),
    },
    {
      key: "openOpportunities",
      header: t("column.openOpportunities"),
      align: "right",
      render: (row) =>
        row.openOpportunities > 0 ? (
          // A count you cannot open is a count you cannot act on, so it clicks through
          // to the filtered list. Re-points at the board in phase 2.
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              void navigate(`/opportunities?opportunities:client=${row.id}`);
            }}
            className="text-primary hover:underline"
          >
            {row.openOpportunities}
          </button>
        ) : (
          <span className="text-muted-foreground">0</span>
        ),
    },
    {
      key: "openPipelineValue",
      header: t("column.openPipeline"),
      align: "right",
      sortable: true,
      render: (row) => formatCurrency(row.openPipelineValue),
    },
  ];

  return (
    <>
      {/* The creating action lives in the header, as it does on Pipeline and Users: the
          toolbar row is for the controls that shape *what is on screen*, and a "New client"
          that moved from screen to screen would be a control nobody learns the place of. */}
      {/* One control row, as on the pipeline (2026-08-25, specs/pipeline-header.md): search
          and filters at the left, the creating action at the right. The heading stays for the
          outline and for a screen reader; the tab already carries it. */}
      <h1 className="sr-only">{t("clients.title")}</h1>

      <ListToolbar
        table={table}
        defs={FILTERS}
        searchPlaceholder={t("clients.searchPlaceholder")}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t("clients.new")}
          </Button>
        }
      />

      {query.isPending && <LoadingState rows={6} label={t("clients.loading")} />}

      {query.error && (
        <ErrorState
          message={query.error instanceof ApiClientError ? query.error.message : t("clients.loadFailed")}
          requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && query.data.items.length === 0 && table.activeFilterCount === 0 && (
        <EmptyState
          title={t("clients.emptyTitle")}
          description={t("clients.emptyDescription")}
          action={{ label: t("clients.new"), onClick: () => setCreateOpen(true) }}
        />
      )}

      {query.data && query.data.items.length === 0 && table.activeFilterCount > 0 && (
        <NoResultState activeFilters={table.activeFilterLabels} onClear={table.clearFilters} />
      )}

      {query.data && query.data.items.length > 0 && (
        <>
          <DataTable
            columns={columns}
            rows={query.data.items}
            rowKey={(row) => row.id}
            sort={table.sort}
            onToggleSort={table.toggleSort}
            onRowClick={(row) => openDetail(`/client/${row.slug}`)}
            caption={t("clients.caption")}
          />
          <PaginationControl
            page={query.data.page}
            perPage={query.data.perPage}
            total={query.data.total}
            onPageChange={table.setPage}
            onPerPageChange={table.setPerPage}
          />
        </>
      )}

      {createOpen && (
        <ClientFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          mode={{ kind: "create" }}
          onCreated={(client) => {
            // The duplicate check returns id and name only, so this routes through the id
            // redirect rather than growing the check's response a field for one link.
            if (session.data?.role === ROLES.headOfSales) void navigate(`/clients/${client.id}`);
          }}
        />
      )}
    </>
  );
}
